import { useState, useEffect } from 'react';
import { getInvoiceOptions } from 'api/provider';
import dayjs from 'utils/dayjs';
import { isOpPortal } from 'utils/common';

interface VisitOption {
    id: string;
    description: string;
    price?: number;
    time?: string;
    isTextVisit?: boolean;
}

interface ServiceOption {
    id: string;
    description: string;
    price: number;
}

interface OtherOption {
    id: string;
    description: string;
}

const useGetInvoiceOptions = (state: string, clientId?: number) => {
    const [visitOptions, setVisitOptions] = useState<VisitOption[]>([]);
    const [serviceOptions, setServiceOptions] = useState<ServiceOption[]>([]);
    const [otherOptions, setOtherOptions] = useState<OtherOption[]>([]);
    const [loading, setLoading] = useState(false);

    const formatTime = (startAt: string, endAt: string) => {
        if (!startAt) return '';

        // Convert UTC timestamp to PST timezone
        const start = dayjs.utc(startAt).tz('America/Los_Angeles');
        const end = dayjs.utc(endAt).tz('America/Los_Angeles');

        // Format time range: "10:00 - 10:15 am (PST)"
        // Start time without am/pm, end time with am/pm
        const startTime = start.format('h:mm');
        const endTime = end.format('h:mm a');
        const timeRange = `${startTime} - ${endTime}`;
        const timezone = '(PST)';

        // Format date: "on Sunday, March 2, 2025"
        const dateStr = start.format('dddd, MMMM D, YYYY');

        return `${timeRange} ${timezone} on ${dateStr}`;
    };

    const getRecentData = (data: { providerAppointments: any[]; textVisits: any[] }) => {
        const dataList = [...data.providerAppointments, ...data.textVisits];
        return dataList.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5);
    };

    useEffect(() => {
        const fetchOptions = async () => {
            if (isOpPortal()) {
                return;
            }
            setLoading(true);
            const res = await getInvoiceOptions(state, clientId);
            if (!res.error && res.data?.data) {
                setOtherOptions(
                    res.data.data.otherDescriptionList.map((item: string, index: number) => ({
                        id: index.toString(),
                        description: item,
                    })) || [],
                );
                setServiceOptions(
                    res.data.data.serviceList.map((item: any) => ({
                        id: item.itemId.toString(),
                        description: `[${item.state}] ${item.serviceSpecialtyName}: ${item.serviceType}`,
                        price: item.patientCost,
                    })) || [],
                );
                setVisitOptions(
                    getRecentData(res.data.data.visit).map((item: any) => ({
                        id: item.id.toString(),
                        description: item.textVisitUuid
                            ? 'Text Visit: Prescription Refill'
                            : item.appointmentName ||
                              `[${item.state}] ${item.serviceSpecialtyName}: ${item.serviceType}`,
                        price: item.serviceItemPatientCost,
                        time: item.textVisitUuid ? undefined : formatTime(item.startAt, item.endAt),
                        isTextVisit: !!item.textVisitUuid,
                    })) || [],
                );
            }
        };
        if (state || clientId) {
            fetchOptions();
        }
    }, [state, clientId]);
    return { visitOptions, serviceOptions, otherOptions, loading };
};

export default useGetInvoiceOptions;
